import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Landmark, Plus, X, Camera, Loader, ExternalLink, Save, RefreshCw, FileText,
  RotateCcw, ShieldAlert, Trash2, Undo2, CheckCircle2, AlertTriangle, Pencil, Paperclip, CreditCard, Copy,
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import {
  api, DOC_URL, EASYPAY_URL, BPAY_BILLER, fmtDate, fmtDay, fmtMoney, fileToBase64, checkFile, copyText,
  labelStyle, inputStyle, th, td, num, GREEN, AMBER, RED,
} from '../utils/atoShared';
import TaxPaidTab from './AtoTaxTab';
import TaxReturnsTab from './AtoReturnsTab';
import TaxDocsTab from './AtoDocsTab';
import {
  workPlan, afterPayment, suggestPlan, planFromExtract, tidyName, todayIso, daysBetween,
  ENTITY_TYPES, ACCOUNTS, cents, dollars,
} from '../utils/atoPlan';

// ── ATO payment plans ───────────────────────────────────────────────────────────
// Paul's private tracker for the family's ATO payment plans — his, Holly's and the
// businesses'. Each plan is the schedule the ATO agreed; each payment is a bank receipt
// the engine reads for the date and amount. The page answers the two questions that
// matter every month: how much is left to draw down, and what is due next (and by when,
// allowing the two business days VLL asked for so a BPAY never lands late).

const OWNER = 'BOUVENGP2';

const STATUS = {
  paid: { label: 'Paid', color: GREEN },
  late: { label: 'Paid late', color: AMBER },
  part: { label: 'Part-paid', color: AMBER },
  overdue: { label: 'Overdue', color: RED },
  due: { label: 'Next due', color: 'var(--hp-gold)' },
  upcoming: { label: 'Upcoming', color: 'var(--hp-dim)' },
};
const Pill = ({ s }) => {
  const x = STATUS[s] || STATUS.upcoming;
  return <span style={{ fontSize: '.7rem', fontWeight: 700, color: x.color, border: `1px solid ${x.color}`, borderRadius: 999, padding: '1px 8px', whiteSpace: 'nowrap' }}>{x.label}</span>;
};

const planTitle = (p) => `${p.entity}${p.label ? ` — ${p.label}` : ''}`;

// Where a plan stands, in one plain sentence and a colour.
function standing(w, today) {
  if (w.done) return { color: GREEN, text: 'Paid in full' };
  if (w.overdue.length) return { color: RED, text: `${fmtMoney(w.overdueAmount)} overdue — the ATO may end the plan` };
  if (w.ahead > 0.004) return { color: GREEN, text: `${fmtMoney(w.ahead)} ahead of schedule` };
  if (w.next) {
    const d = daysBetween(today, w.next.payBy);
    if (d < 0) return { color: AMBER, text: `Pay today — the ${fmtDay(w.next.date)} instalment is inside the two-day window` };
    if (d <= 7) return { color: AMBER, text: `Pay ${fmtMoney(w.next.owing)} by ${fmtDay(w.next.payBy)}` };
  }
  return { color: GREEN, text: 'On schedule' };
}

// ── Plan drawer (add / edit, optionally read off an ATO schedule) ────────────────
function PlanDrawer({ plan, onClose, onSaved, notify }) {
  const editing = !!plan;
  const [form, setForm] = useState(() => plan ? {
    ...plan, instalments: (plan.instalments || []).map((i) => ({ ...i })),
  } : {
    entity: '', entityType: 'Individual', account: ACCOUNTS[0], label: '', prn: '', debt: '', planTotal: '',
    created: todayIso(), frequency: 'Monthly', agent: '', notes: '', instalments: [],
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [gen, setGen] = useState({ first: '', count: '', amount: '', final: '' });

  const readSchedule = async (f) => {
    if (!checkFile(f, notify)) return;
    setBusy(true);
    try {
      const { base64, contentType } = await fileToBase64(f);
      setFile({ base64, contentType, name: f.name });
      const r = await api('readSchedule', { base64, contentType });
      if (r.success) {
        const x = planFromExtract(r.extracted);
        setForm((cur) => ({
          ...cur,
          entity: cur.entity || tidyName(x.entity),
          account: cur.account && cur.account !== ACCOUNTS[0] ? cur.account : (/income tax/i.test(x.account) ? ACCOUNTS[0] : (x.account || cur.account)),
          debt: cur.debt || x.debt, planTotal: x.planTotal || cur.planTotal,
          created: x.created || cur.created, frequency: x.frequency || cur.frequency,
          instalments: x.instalments.length ? x.instalments : cur.instalments,
        }));
        notify?.(`Read ${x.instalments.length} instalments — check them, then save`, 'success');
      } else notify?.(`${r.error || "Couldn't read the schedule"}. Enter it by hand — the file still saves with the plan.`, 'info');
    } catch (e) { notify?.(`Could not process the file: ${e.message}`, 'error'); }
    finally { setBusy(false); }
  };

  // For a plan agreed by phone with no print-out: N equal instalments + a final one.
  const generate = () => {
    const n = parseInt(gen.count, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(gen.first) || !(n > 0) || !(Number(gen.amount) > 0)) { notify?.('Enter a first date, how many instalments and the amount', 'error'); return; }
    const step = form.frequency === 'Weekly' ? 7 : form.frequency === 'Fortnightly' ? 14 : 0;
    const rows = [];
    const total = n + (Number(gen.final) > 0 ? 1 : 0);
    for (let k = 0; k < total; k++) {
      const d = new Date(gen.first + 'T12:00');
      if (step) d.setDate(d.getDate() + step * k); else d.setMonth(d.getMonth() + k);
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      rows.push({ date: iso, amount: k < n ? Number(gen.amount) : Number(gen.final) });
    }
    setForm((f) => ({ ...f, instalments: rows, planTotal: f.planTotal || dollars(rows.reduce((a, r) => a + cents(r.amount), 0)) }));
  };

  const setRow = (i, k, v) => setForm((f) => ({ ...f, instalments: f.instalments.map((r, j) => (j === i ? { ...r, [k]: v } : r)) }));
  const schedTotal = dollars((form.instalments || []).reduce((a, r) => a + cents(r.amount), 0));
  const mismatch = form.planTotal !== '' && cents(form.planTotal) !== cents(schedTotal) && form.instalments.length > 0;

  const save = async () => {
    setSaving(true);
    try {
      const r = await api('savePlan', {
        plan: form,
        fileBase64: file?.base64, fileContentType: file?.contentType, fileName: file?.name, fileKind: 'ATO schedule',
      });
      if (!r.success) { notify?.(r.error || 'Save failed', 'error'); return; }
      notify?.(editing ? 'Plan updated' : 'Plan added', 'success');
      onSaved(r.plan);
    } catch (e) { notify?.(`Error: ${e.message}`, 'error'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }} onClick={onClose}>
      <div style={{ width: 600, maxWidth: '100%', background: 'var(--hp-bg)', borderLeft: '1px solid var(--hp-border)', height: '100%', overflowY: 'auto', padding: 24 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.65rem', textTransform: 'uppercase', letterSpacing: '.12em', color: 'var(--hp-gold)', marginBottom: 4 }}>ATO payment plan</div>
            <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{editing ? `Edit — ${planTitle(plan)}` : 'Add a payment plan'}</h3>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        <div style={{ border: '2px dashed var(--hp-border)', borderRadius: 10, padding: 14, marginBottom: 18, textAlign: 'center' }}
          onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); readSchedule(e.dataTransfer?.files?.[0]); }}>
          <div style={{ color: 'var(--hp-dim)', fontSize: '.84rem', marginBottom: 8 }}>
            {busy ? <><Loader size={14} /> Reading the schedule…</>
              : file ? <><FileText size={14} /> {file.name} — saves with the plan</>
              : "Drop the ATO's “Payment plans” print-out (PDF or screenshot) and the instalments fill themselves in."}
          </div>
          <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
            <input type="file" accept="image/*,application/pdf" style={{ display: 'none' }} disabled={busy}
              onChange={(e) => { readSchedule(e.target.files?.[0]); e.target.value = ''; }} />
            <Paperclip size={13} /> {file ? 'Use a different file' : 'Read an ATO schedule'}
          </label>
        </div>

        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div><label style={labelStyle}>Who (entity)</label><input className="input" style={inputStyle} value={form.entity} onChange={(e) => set('entity', e.target.value)} placeholder="e.g. Holly Bouveng / PH Bouveng Pty Ltd" /></div>
          <div><label style={labelStyle}>Entity type</label>
            <select className="input" style={inputStyle} value={form.entityType} onChange={(e) => set('entityType', e.target.value)}>{ENTITY_TYPES.map((t) => <option key={t}>{t}</option>)}</select></div>
          <div><label style={labelStyle}>Account</label>
            <select className="input" style={inputStyle} value={ACCOUNTS.includes(form.account) ? form.account : 'Other'} onChange={(e) => set('account', e.target.value)}>{ACCOUNTS.map((t) => <option key={t}>{t}</option>)}</select></div>
          <div><label style={labelStyle}>Label</label><input className="input" style={inputStyle} value={form.label} onChange={(e) => set('label', e.target.value)} placeholder="e.g. 2025 ITA" /></div>
          <div><label style={labelStyle}>Payment reference (PRN)</label><input className="input" style={inputStyle} value={form.prn} onChange={(e) => set('prn', e.target.value)} placeholder="From the ATO statement / portal — used on EasyPay & BPAY" /></div>
          <div><label style={labelStyle}>Frequency</label>
            <select className="input" style={inputStyle} value={form.frequency} onChange={(e) => set('frequency', e.target.value)}>{['Monthly', 'Fortnightly', 'Weekly'].map((t) => <option key={t}>{t}</option>)}</select></div>
          <div><label style={labelStyle}>Debt ($)</label><input type="number" step="0.01" className="input" style={inputStyle} value={form.debt} onChange={(e) => set('debt', e.target.value)} /></div>
          <div><label style={labelStyle}>Plan total incl. est. GIC ($)</label><input type="number" step="0.01" className="input" style={inputStyle} value={form.planTotal} onChange={(e) => set('planTotal', e.target.value)} /></div>
          <div><label style={labelStyle}>Agreed on</label><input type="date" className="input" style={inputStyle} value={form.created} onChange={(e) => set('created', e.target.value)} /></div>
          <div><label style={labelStyle}>Arranged by</label><input className="input" style={inputStyle} value={form.agent} onChange={(e) => set('agent', e.target.value)} placeholder="e.g. VLL Partners — Patrick Chapman" /></div>
          <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Notes</label><textarea className="input" style={{ ...inputStyle, minHeight: 54 }} value={form.notes} onChange={(e) => set('notes', e.target.value)} /></div>
        </div>

        <div style={{ marginTop: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <label style={{ ...labelStyle, marginBottom: 0 }}>Instalment schedule ({form.instalments.length})</label>
          <span style={{ fontSize: '.8rem', color: mismatch ? AMBER : 'var(--hp-dim)' }}>
            Adds up to {fmtMoney(schedTotal)}{mismatch ? ` — plan total says ${fmtMoney(form.planTotal)}` : ''}
          </span>
        </div>
        {!form.instalments.length && (
          <div className="card" style={{ padding: 12, marginTop: 8 }}>
            <div style={{ fontSize: '.8rem', color: 'var(--hp-dim)', marginBottom: 8 }}>No print-out? Build it: N equal instalments plus a final one.</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 8, alignItems: 'end' }}>
              <div><label style={labelStyle}>First due</label><input type="date" className="input" style={inputStyle} value={gen.first} onChange={(e) => setGen({ ...gen, first: e.target.value })} /></div>
              <div><label style={labelStyle}>How many</label><input type="number" className="input" style={inputStyle} value={gen.count} onChange={(e) => setGen({ ...gen, count: e.target.value })} /></div>
              <div><label style={labelStyle}>Each ($)</label><input type="number" step="0.01" className="input" style={inputStyle} value={gen.amount} onChange={(e) => setGen({ ...gen, amount: e.target.value })} /></div>
              <div><label style={labelStyle}>Final ($)</label><input type="number" step="0.01" className="input" style={inputStyle} value={gen.final} onChange={(e) => setGen({ ...gen, final: e.target.value })} /></div>
              <button className="btn btn-secondary btn-sm" onClick={generate}>Build</button>
            </div>
          </div>
        )}
        {form.instalments.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
            <thead><tr><th style={th}>#</th><th style={th}>Due</th><th style={{ ...th, textAlign: 'right' }}>Amount</th><th style={th} /></tr></thead>
            <tbody>
              {form.instalments.map((r, i) => (
                <tr key={i}>
                  <td style={{ ...td, color: 'var(--hp-dim)' }}>{i + 1}</td>
                  <td style={td}><input type="date" className="input" style={{ ...inputStyle, padding: '4px 8px' }} value={r.date} onChange={(e) => setRow(i, 'date', e.target.value)} /></td>
                  <td style={td}><input type="number" step="0.01" className="input" style={{ ...inputStyle, padding: '4px 8px', textAlign: 'right' }} value={r.amount} onChange={(e) => setRow(i, 'amount', e.target.value)} /></td>
                  <td style={{ ...td, width: 30 }}><button className="btn btn-ghost btn-sm" onClick={() => setForm((f) => ({ ...f, instalments: f.instalments.filter((_, j) => j !== i) }))}><X size={12} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <button className="btn btn-ghost btn-sm" style={{ marginTop: 6 }} onClick={() => setForm((f) => ({ ...f, instalments: [...f.instalments, { date: '', amount: '' }] }))}><Plus size={12} /> Add a row</button>

        <div style={{ display: 'flex', gap: 10, marginTop: 22 }}>
          <button className="btn btn-primary" style={{ flex: 1 }} onClick={save} disabled={saving || busy}>
            {saving ? <><Loader size={14} /> Saving…</> : <><Save size={14} /> {editing ? 'Save changes' : 'Save plan'}</>}
          </button>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Payment drawer (upload a receipt → date + amount read off it) ────────────────
function PaymentDrawer({ payment, plans, payments, defaultPlanId, onClose, onSaved, notify }) {
  const editing = !!payment;
  const live = plans.filter((p) => !p.archived || (payment && p.id === payment.planId));
  const [form, setForm] = useState(() => payment ? { ...payment } : {
    planId: defaultPlanId || (live.length === 1 ? live[0].id : ''), date: '', amount: '', fee: '', method: 'BPAY', reference: '', receiptNo: '', note: '',
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [file, setFile] = useState(null);
  const [extracted, setExtracted] = useState(null);
  const [why, setWhy] = useState('');
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const readReceipt = async (f) => {
    if (!checkFile(f, notify)) return;
    setBusy(true);
    try {
      const { base64, contentType } = await fileToBase64(f);
      setFile({ base64, contentType, name: f.name, dataUrl: contentType === 'application/pdf' ? '' : `data:${contentType};base64,${base64}` });
      const r = await api('readReceipt', { base64, contentType });
      if (r.success) {
        const x = r.extracted;
        setExtracted(x);
        const sug = suggestPlan(plans, x);
        setForm((cur) => ({
          ...cur,
          date: /^\d{4}-\d{2}-\d{2}$/.test(x.date) ? x.date : cur.date,
          amount: Number(x.amount) > 0 ? Number(x.amount) : cur.amount,
          fee: Number(x.fee) > 0 ? Number(x.fee) : '',
          method: ['BPAY', 'Transfer', 'Card', 'Other'].includes(x.method) ? x.method : cur.method,
          reference: x.reference || cur.reference,
          receiptNo: x.receiptNo || cur.receiptNo,
          planId: cur.planId || (sug ? sug.planId : ''),
        }));
        setWhy(sug && !form.planId && !defaultPlanId ? sug.why : '');
        notify?.('Receipt read — check the date and amount, then save', 'success');
      } else notify?.(`${r.error || "Couldn't read the receipt"}. Enter the date and amount — the receipt still saves.`, 'info');
    } catch (e) { notify?.(`Could not process the file: ${e.message}`, 'error'); }
    finally { setBusy(false); }
  };

  const plan = plans.find((p) => p.id === form.planId);
  const others = payments.filter((p) => p.id !== payment?.id);
  const preview = plan && Number(form.amount) > 0 && /^\d{4}-\d{2}-\d{2}$/.test(form.date)
    ? afterPayment(plan, others, { ...form, id: payment?.id || 'preview' }) : null;
  const refMismatch = plan?.prn && form.reference && form.reference.replace(/\D/g, '') && !form.reference.replace(/\D/g, '').includes(plan.prn.replace(/\D/g, ''));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api('savePayment', {
        payment: { ...form, extracted: extracted || form.extracted },
        fileBase64: file?.base64, fileContentType: file?.contentType,
        fileName: file ? `${(plan?.entity || 'ATO').replace(/[^\w.\- ]/g, '_')} ATO payment ${form.date || ''}` : undefined,
      });
      if (!r.success) { notify?.(r.error || 'Save failed', 'error'); return; }
      if (preview) {
        const a = preview.after;
        notify?.(a.done
          ? `${plan.entity}: plan paid in full.`
          : `${plan.entity}: ${fmtMoney(a.remaining)} left to pay${a.next ? ` · next ${fmtMoney(a.next.owing)} due ${fmtDay(a.next.date)} (pay by ${fmtDay(a.next.payBy)})` : ''}`, 'success');
      } else notify?.('Payment saved', 'success');
      onSaved(r.payment);
    } catch (e) { notify?.(`Error: ${e.message}`, 'error'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }} onClick={onClose}>
      <div style={{ width: 520, maxWidth: '100%', background: 'var(--hp-bg)', borderLeft: '1px solid var(--hp-border)', height: '100%', overflowY: 'auto', padding: 24 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.65rem', textTransform: 'uppercase', letterSpacing: '.12em', color: 'var(--hp-gold)', marginBottom: 4 }}>ATO payment</div>
            <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{editing ? 'Edit payment' : 'Record a payment'}</h3>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        <div style={{ border: '2px dashed var(--hp-border)', borderRadius: 10, padding: 14, marginBottom: 18, textAlign: 'center' }}
          onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); readReceipt(e.dataTransfer?.files?.[0]); }}>
          {file?.dataUrl && <img src={file.dataUrl} alt="receipt" style={{ maxHeight: 180, maxWidth: '100%', borderRadius: 6, border: '1px solid var(--hp-border)', marginBottom: 8 }} />}
          <div style={{ color: 'var(--hp-dim)', fontSize: '.84rem', marginBottom: 8 }}>
            {busy ? <><Loader size={14} /> Reading the receipt…</>
              : file ? <><FileText size={14} /> {file.name}</>
              : editing && payment.file ? <>
                <a href={`${DOC_URL}?payment=${payment.id}`} target="_blank" rel="noreferrer"><ExternalLink size={13} /> View stored receipt</a>
                {(payment.priorFiles || []).map((f, n) => (
                  <div key={n} style={{ fontSize: '.76rem', marginTop: 4 }}>
                    <a href={`${DOC_URL}?payment=${payment.id}&prior=${n}`} target="_blank" rel="noreferrer">Earlier copy {n + 1}: {f.name}</a>
                  </div>
                ))}
              </>
              : 'Upload the bank receipt (screenshot, photo or PDF) — the payment date and amount are read off it.'}
          </div>
          <label className="btn btn-primary btn-sm" style={{ cursor: 'pointer' }}>
            <input type="file" accept="image/*,application/pdf" style={{ display: 'none' }} disabled={busy}
              onChange={(e) => { readReceipt(e.target.files?.[0]); e.target.value = ''; }} />
            <Camera size={13} /> {file || (editing && payment.file) ? 'Replace receipt (the old copy is kept)' : 'Upload receipt'}
          </label>
        </div>

        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Plan</label>
            <select className="input" style={inputStyle} value={form.planId} onChange={(e) => { set('planId', e.target.value); setWhy(''); }}>
              <option value="">— which plan? —</option>
              {live.map((p) => <option key={p.id} value={p.id}>{planTitle(p)}</option>)}
            </select>
            {why && <div style={{ fontSize: '.74rem', color: 'var(--hp-dim)', marginTop: 4 }}>Picked by {why} — change it if that's wrong.</div>}
          </div>
          <div><label style={labelStyle}>Payment date</label><input type="date" className="input" style={inputStyle} value={form.date} onChange={(e) => set('date', e.target.value)} /></div>
          <div><label style={labelStyle}>Amount to the ATO ($)</label><input type="number" step="0.01" className="input" style={inputStyle} value={form.amount} onChange={(e) => set('amount', e.target.value)} /></div>
          <div><label style={labelStyle}>Card fee ($) — not credited</label><input type="number" step="0.01" className="input" style={inputStyle} value={form.fee} onChange={(e) => set('fee', e.target.value)} placeholder="0.00 for BPAY" /></div>
          {Number(form.fee) > 0 && <div style={{ gridColumn: '1 / -1', fontSize: '.76rem', color: 'var(--hp-dim)', marginTop: -4 }}>
            Card charged {fmtMoney(dollars(cents(form.amount) + cents(form.fee)))}. Only the {fmtMoney(form.amount)} reaches the ATO — the fee is the card processor's, so it's kept on record but doesn't reduce the plan.
          </div>}
          <div><label style={labelStyle}>Method</label>
            <select className="input" style={inputStyle} value={form.method} onChange={(e) => set('method', e.target.value)}>{['BPAY', 'Transfer', 'Card', 'Other'].map((t) => <option key={t}>{t}</option>)}</select></div>
          <div><label style={labelStyle}>Receipt no.</label><input className="input" style={inputStyle} value={form.receiptNo} onChange={(e) => set('receiptNo', e.target.value)} /></div>
          <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Payment reference (PRN)</label><input className="input" style={inputStyle} value={form.reference} onChange={(e) => set('reference', e.target.value)} />
            {refMismatch && <div style={{ fontSize: '.76rem', color: AMBER, marginTop: 4 }}><AlertTriangle size={12} /> This reference doesn't match the plan's PRN ({plan.prn}) — a payment to the wrong PRN lands on someone else's account.</div>}
          </div>
          <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Note</label><input className="input" style={inputStyle} value={form.note} onChange={(e) => set('note', e.target.value)} /></div>
        </div>

        {preview && (
          <div className="card" style={{ padding: 14, marginTop: 18, borderColor: 'var(--hp-gold)' }}>
            <div style={{ fontSize: '.72rem', color: 'var(--hp-gold)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 6 }}>After this payment</div>
            <div style={{ fontSize: '1.3rem', fontWeight: 800 }}>{fmtMoney(preview.after.remaining)} <span style={{ fontSize: '.85rem', fontWeight: 400, color: 'var(--hp-dim)' }}>left to pay (was {fmtMoney(preview.before.remaining)})</span></div>
            <div style={{ fontSize: '.84rem', color: 'var(--hp-dim)', marginTop: 4 }}>
              {preview.after.done ? 'That clears the plan.'
                : preview.after.next ? <>Next: {fmtMoney(preview.after.next.owing)} due {fmtDay(preview.after.next.date)} — pay by {fmtDay(preview.after.next.payBy)}. {preview.after.instalmentsLeft} instalment{preview.after.instalmentsLeft === 1 ? '' : 's'} to go.</> : null}
              {preview.after.overpaid > 0 && <div style={{ color: AMBER }}>That's {fmtMoney(preview.after.overpaid)} more than the plan total — check the amount.</div>}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button className="btn btn-primary" style={{ flex: 1 }} onClick={save} disabled={saving || busy}>
            {saving ? <><Loader size={14} /> Saving…</> : <><Save size={14} /> {editing ? 'Save changes' : 'Save payment'}</>}
          </button>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

const plainAmount = (n) => (Number(n) || 0).toFixed(2);

// The PRN, big enough to read off the screen, with a copy button — or a prompt to add it.
function PrnLine({ plan, notify, onEdit }) {
  const stop = (e) => e.stopPropagation();
  if (!plan.prn) {
    return (
      <div onClick={stop} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: '.8rem', color: AMBER }}>
        <AlertTriangle size={13} /> No PRN recorded
        <button className="btn btn-ghost btn-sm" onClick={onEdit}><Pencil size={12} /> Add PRN</button>
      </div>
    );
  }
  const tag = { fontSize: '.68rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em', minWidth: 78 };
  const val = { fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '.98rem', letterSpacing: '.04em' };
  const copy = (text, what) => async () => notify?.((await copyText(text)) ? `${what} copied` : 'Could not copy — select it by hand', 'success');
  return (
    <div onClick={stop} style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={tag}>PRN / Ref</span>
        <span style={val}>{plan.prn}</span>
        <button className="btn btn-ghost btn-sm" title="Copy PRN" onClick={copy(plan.prn, 'PRN')}><Copy size={12} /></button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={tag}>BPAY biller</span>
        <span style={val}>{BPAY_BILLER}</span>
        <button className="btn btn-ghost btn-sm" title="Copy BPAY biller code" onClick={copy(BPAY_BILLER, 'Biller code')}><Copy size={12} /></button>
      </div>
    </div>
  );
}

// Open EasyPay with the PRN already on the clipboard, so paying is: paste, amount, card.
async function payOnEasyPay(plan, w, notify) {
  // Both must START synchronously inside the click: the copy while this page still has
  // focus (a background tab can't write the clipboard), and the open before any await
  // (Safari treats a later window.open as a pop-up).
  const copied = plan.prn ? copyText(plan.prn) : Promise.resolve(false);
  window.open(EASYPAY_URL, '_blank', 'noopener');
  if (plan.prn && (await copied)) {
    notify?.(`PRN ${plan.prn} copied — paste it into EasyPay${w.next ? `, amount ${fmtMoney(w.next.owing)}` : ''}`, 'success');
  } else if (!plan.prn) {
    notify?.('No PRN recorded for this plan — EasyPay needs it. Add it on the plan.', 'info');
  }
}

// ── One plan's card ────────────────────────────────────────────────────────────
function PlanCard({ plan, w, today, selected, onSelect, onPay, onEdit, notify }) {
  const st = standing(w, today);
  return (
    <div className="card" onClick={onSelect} style={{ padding: 16, cursor: 'pointer', borderColor: selected ? 'var(--hp-gold)' : undefined, opacity: plan.archived ? 0.6 : 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <div>
          <div style={{ fontWeight: 800 }}>{plan.entity}</div>
          <div style={{ fontSize: '.76rem', color: 'var(--hp-dim)' }}>{[plan.label, plan.account].filter(Boolean).join(' · ')}</div>
        </div>
      </div>
      <PrnLine plan={plan} notify={notify} onEdit={onEdit} />
      <div style={{ marginTop: 12, fontSize: '.7rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em' }}>Left to pay</div>
      <div style={{ fontSize: '1.6rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(w.remaining)}</div>
      <div style={{ height: 6, background: 'var(--hp-border)', borderRadius: 3, overflow: 'hidden', margin: '8px 0 6px' }}>
        <div style={{ width: `${w.pctPaid}%`, height: '100%', background: GREEN }} />
      </div>
      <div style={{ fontSize: '.78rem', color: 'var(--hp-dim)' }}>{fmtMoney(w.paid)} paid of {fmtMoney(w.planTotal)} · {w.pctPaid}%</div>
      {w.next && !w.done && (
        <div style={{ fontSize: '.82rem', marginTop: 8 }}>
          Next <strong>{fmtMoney(w.next.owing)}</strong> due {fmtDay(w.next.date)} <span style={{ color: 'var(--hp-dim)' }}>· pay by {fmtDay(w.next.payBy)}</span>
        </div>
      )}
      <div style={{ fontSize: '.8rem', marginTop: 6, color: st.color, fontWeight: 600 }}>{st.text}</div>
      {!w.done && !plan.archived && (
        <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <button className="btn btn-primary btn-sm" style={{ flex: '1 1 auto' }} onClick={() => payOnEasyPay(plan, w, notify)} title="Opens the ATO's Government EasyPay (card payment) and copies the PRN">
            <CreditCard size={13} /> Pay on EasyPay <ExternalLink size={11} />
          </button>
          {w.next && (
            <button className="btn btn-ghost btn-sm" title="Copy the next instalment amount"
              onClick={async () => notify?.((await copyText(plainAmount(w.next.owing))) ? `Amount ${fmtMoney(w.next.owing)} copied` : 'Could not copy', 'success')}>
              <Copy size={12} /> {fmtMoney(w.next.owing)}
            </button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={onPay} title="Upload the receipt once it's paid"><Plus size={13} /> Record payment</button>
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function AtoPlansPage() {
  const { currentUser, addNotification } = useApp();
  const isOwner = (currentUser?.login || '').toUpperCase() === OWNER;
  const [plans, setPlans] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selId, setSelId] = useState('');
  const [planDrawer, setPlanDrawer] = useState(null); // { plan } | { plan: null }
  const [payDrawer, setPayDrawer] = useState(null);   // { payment, planId }
  const [showArchived, setShowArchived] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  // Four tabs: the payment plans, every other tax paid (BAS, notices, assessments), and
  // the vault: lodged tax returns and tax documents / correspondence, both behind the
  // same vault password. The hash keeps the tab across a refresh: /ato-plans#tax,
  // #returns, #docs.
  const [tab, setTabState] = useState(() => {
    const h = typeof window !== 'undefined' ? window.location.hash : '';
    return ['#tax', '#returns', '#docs'].includes(h) ? h.slice(1) : 'plans';
  });
  const setTab = (t) => { setTabState(t); try { window.history.replaceState(null, '', t === 'plans' ? window.location.pathname : `#${t}`); } catch { /* ignore */ } };
  const today = todayIso();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api('load');
      if (r.success) { setPlans(r.plans); setPayments(r.payments); }
      else addNotification?.(`Could not load plans: ${r.error || 'unknown error'}`, 'error');
    } catch (e) { addNotification?.(`Error: ${e.message}`, 'error'); }
    finally { setLoading(false); }
  }, [addNotification]);
  useEffect(() => { if (isOwner) load(); }, [isOwner, load]);

  const worked = useMemo(() => Object.fromEntries(plans.map((p) => [p.id, workPlan(p, payments, today)])), [plans, payments, today]);
  const shown = plans.filter((p) => showArchived || !p.archived);
  const sel = plans.find((p) => p.id === selId) || shown[0] || null;
  const w = sel ? worked[sel.id] : null;

  const active = plans.filter((p) => !p.archived);
  const totalLeft = active.reduce((a, p) => a + cents(worked[p.id].remaining), 0);
  const totalPaid = active.reduce((a, p) => a + cents(worked[p.id].paid), 0);
  // What falls due in the next 31 days across every plan — the monthly cash call.
  const horizon = (() => { const d = new Date(today + 'T12:00'); d.setDate(d.getDate() + 31); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();
  const dueSoon = active.flatMap((p) => worked[p.id].instalments.filter((i) => i.owing > 0 && i.date <= horizon).map((i) => ({ ...i, plan: p })))
    .sort((a, b) => a.date.localeCompare(b.date));

  const toggleDeleted = async (pay, deleted) => {
    const r = await api('setPaymentDeleted', { id: pay.id, deleted });
    if (!r.success) { addNotification?.(r.error || 'Failed', 'error'); return; }
    setPayments((ps) => ps.map((x) => (x.id === pay.id ? r.payment : x)));
    addNotification?.(deleted ? 'Payment removed — Undo is under “Show removed”' : 'Payment restored', 'success');
  };
  const archive = async (plan, archived) => {
    const r = await api('archivePlan', { planId: plan.id, archived });
    if (r.success) setPlans((ps) => ps.map((x) => (x.id === plan.id ? r.plan : x)));
  };
  const addDoc = async (plan, f) => {
    if (!checkFile(f, addNotification)) return;
    const { base64, contentType } = await fileToBase64(f);
    const r = await api('addPlanDoc', { planId: plan.id, fileBase64: base64, fileContentType: contentType, fileName: f.name, fileKind: 'Document' });
    if (r.success) { setPlans((ps) => ps.map((x) => (x.id === plan.id ? r.plan : x))); addNotification?.('Document attached', 'success'); }
    else addNotification?.(r.error || 'Upload failed', 'error');
  };

  if (!isOwner) {
    return (
      <>
        <div className="page-header"><div className="page-header-left"><h2>ATO Payment Plans</h2></div></div>
        <div className="page-body">
          <div className="card" style={{ padding: 28, display: 'flex', alignItems: 'center', gap: 12 }}>
            <ShieldAlert size={22} color="var(--hp-gold)" />
            <div><div style={{ fontWeight: 700 }}>Private</div><div style={{ color: 'var(--hp-dim)', fontSize: '.88rem' }}>This page is private to Paul.</div></div>
          </div>
        </div>
      </>
    );
  }

  const planPays = sel ? payments.filter((p) => p.planId === sel.id && (showDeleted || !p.deletedAt)).sort((a, b) => b.date.localeCompare(a.date)) : [];
  const removedCount = sel ? payments.filter((p) => p.planId === sel.id && p.deletedAt).length : 0;

  const tabBtn = (k, label) => (
    <button key={k} className={`btn btn-sm ${tab === k ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTab(k)}>{label}</button>
  );
  const tabs = <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>{tabBtn('plans', 'Payment plans')}{tabBtn('tax', 'Tax paid')}{tabBtn('returns', 'Tax returns')}{tabBtn('docs', 'Tax documents')}</div>;
  // Entities already known, so the tax tab's "Who" suggests the same spellings.
  const knownEntities = Array.from(new Set(plans.map((p) => p.entity).filter(Boolean)));

  if (tab === 'returns' || tab === 'docs') {
    return (
      <>
        <div className="page-header">
          <div className="page-header-left">
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}><Landmark size={22} style={{ color: 'var(--hp-gold)' }} /> ATO Payment Plans</h2>
            <p>{tab === 'returns'
              ? 'Tax returns — the lodged returns for every entity, behind your vault password and stored encrypted'
              : 'Tax documents — ATO letters, notices and other tax correspondence, behind your vault password and stored encrypted'}</p>
          </div>
        </div>
        <div className="page-body">
          {tabs}
          {tab === 'returns'
            ? <TaxReturnsTab notify={addNotification} knownEntities={knownEntities} />
            : <TaxDocsTab notify={addNotification} knownEntities={knownEntities} />}
        </div>
      </>
    );
  }

  if (tab === 'tax') {
    return (
      <>
        <div className="page-header">
          <div className="page-header-left">
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}><Landmark size={22} style={{ color: 'var(--hp-gold)' }} /> ATO Payment Plans</h2>
            <p>Tax paid — activity statements, reminder notices and assessments, with the receipt for each payment</p>
          </div>
        </div>
        <div className="page-body">
          {tabs}
          <TaxPaidTab notify={addNotification} knownEntities={knownEntities} />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}><Landmark size={22} style={{ color: 'var(--hp-gold)' }} /> ATO Payment Plans</h2>
          <p>{fmtMoney(dollars(totalLeft))} left to pay across {active.length} plan{active.length === 1 ? '' : 's'} · {fmtMoney(dollars(totalPaid))} paid so far</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary btn-sm" onClick={load} title="Refresh"><RefreshCw size={14} /></button>
          <button className="btn btn-secondary" onClick={() => setPlanDrawer({ plan: null })}><Plus size={14} /> Add plan</button>
          <button className="btn btn-primary" onClick={() => setPayDrawer({ payment: null, planId: '' })} disabled={!active.length}><Camera size={14} /> Record payment</button>
        </div>
      </div>

      <div className="page-body">
        {tabs}
        {loading ? (
          <div className="card" style={{ padding: 24, color: 'var(--hp-dim)' }}><Loader size={15} /> Loading…</div>
        ) : !plans.length ? (
          <div className="card" style={{ padding: 24, color: 'var(--hp-dim)' }}>No plans yet. <strong>Add plan</strong> and drop in the ATO's schedule.</div>
        ) : (
          <>
            {dueSoon.length > 0 && (
              <div className="card" style={{ padding: 14, marginBottom: 16 }}>
                <div style={{ ...labelStyle, marginBottom: 8 }}>Due in the next month</div>
                {dueSoon.map((i) => (
                  <div key={i.plan.id + i.date} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '5px 0', borderTop: '1px solid var(--hp-border)', fontSize: '.86rem', flexWrap: 'wrap' }}>
                    <span><strong>{i.plan.entity}</strong> <span style={{ color: 'var(--hp-dim)' }}>· due {fmtDay(i.date)} · pay by {fmtDay(i.payBy)}</span></span>
                    <span style={num}>{fmtMoney(i.owing)} {i.status === 'overdue' && <Pill s="overdue" />}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 6, borderTop: '1px solid var(--hp-border)', fontWeight: 700 }}>
                  <span>Total</span><span style={num}>{fmtMoney(dollars(dueSoon.reduce((a, i) => a + cents(i.owing), 0)))}</span>
                </div>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14, marginBottom: 10 }}>
              {shown.map((p) => (
                <PlanCard key={p.id} plan={p} w={worked[p.id]} today={today} selected={sel && sel.id === p.id} notify={addNotification}
                  onSelect={() => setSelId(p.id)} onPay={() => setPayDrawer({ payment: null, planId: p.id })}
                  onEdit={() => setPlanDrawer({ plan: p })} />
              ))}
            </div>
            {plans.some((p) => p.archived) && (
              <label style={{ fontSize: '.8rem', color: 'var(--hp-dim)', display: 'flex', gap: 6, alignItems: 'center', marginBottom: 14 }}>
                <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Show archived plans
              </label>
            )}

            {sel && w && (
              <div className="card" style={{ padding: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
                  <div>
                    <h3 style={{ margin: 0 }}>{planTitle(sel)}</h3>
                    <div style={{ fontSize: '.8rem', color: 'var(--hp-dim)', marginTop: 3 }}>
                      {[sel.entityType, sel.account, sel.frequency, sel.created && `agreed ${fmtDate(sel.created)}`, sel.agent].filter(Boolean).join(' · ')}
                    </div>
                    <div style={{ fontSize: '.8rem', color: 'var(--hp-dim)' }}>
                      Debt {fmtMoney(sel.debt)} · plan total {fmtMoney(w.planTotal)}{sel.debt ? ` (est. GIC ${fmtMoney(dollars(cents(w.planTotal) - cents(sel.debt)))})` : ''}
                      {sel.prn ? <> · PRN <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--hp-text)' }}>{sel.prn}</strong> · BPAY biller <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--hp-text)' }}>{BPAY_BILLER}</strong></> : <span style={{ color: AMBER }}> · no PRN recorded</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {!w.done && !sel.archived && <button className="btn btn-primary btn-sm" onClick={() => payOnEasyPay(sel, w, addNotification)}><CreditCard size={13} /> Pay on EasyPay <ExternalLink size={11} /></button>}
                    <button className="btn btn-ghost btn-sm" onClick={() => setPlanDrawer({ plan: sel })}><Pencil size={13} /> Edit</button>
                    <label className="btn btn-ghost btn-sm" style={{ cursor: 'pointer' }}>
                      <input type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={(e) => { addDoc(sel, e.target.files?.[0]); e.target.value = ''; }} />
                      <Paperclip size={13} /> Attach
                    </label>
                    <button className="btn btn-ghost btn-sm" onClick={() => archive(sel, !sel.archived)}>{sel.archived ? 'Unarchive' : 'Archive'}</button>
                  </div>
                </div>
                {(sel.docs || []).length > 0 && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                    {sel.docs.map((d) => (
                      <a key={d.id} className="btn btn-secondary btn-sm" href={`${DOC_URL}?plan=${sel.id}&doc=${d.id}`} target="_blank" rel="noreferrer"><FileText size={12} /> {d.kind}: {d.name}</a>
                    ))}
                  </div>
                )}
                {w.scheduleMismatch && (
                  <div style={{ fontSize: '.8rem', color: AMBER, marginTop: 10 }}><AlertTriangle size={12} /> The instalments add up to {fmtMoney(w.scheduleTotal)}, not the plan total of {fmtMoney(w.planTotal)}.</div>
                )}
                {sel.notes && <div style={{ fontSize: '.84rem', marginTop: 10, whiteSpace: 'pre-wrap' }}>{sel.notes}</div>}

                <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 18, marginTop: 16 }}>
                  <div style={{ overflowX: 'auto' }}>
                    <div style={labelStyle}>Instalments</div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 420 }}>
                      <thead><tr><th style={th}>#</th><th style={th}>Due</th><th style={th}>Pay by</th><th style={{ ...th, textAlign: 'right' }}>Amount</th><th style={{ ...th, textAlign: 'right' }}>Owing</th><th style={th} /></tr></thead>
                      <tbody>
                        {w.instalments.map((i) => (
                          <tr key={i.n}>
                            <td style={{ ...td, color: 'var(--hp-dim)' }}>{i.n}</td>
                            <td style={td}>{fmtDate(i.date)}</td>
                            <td style={{ ...td, color: 'var(--hp-dim)' }}>{i.owing > 0 ? fmtDay(i.payBy) : (i.coveredOn ? `paid ${fmtDay(i.coveredOn)}` : '')}</td>
                            <td style={{ ...td, ...num }}>{fmtMoney(i.amount)}</td>
                            <td style={{ ...td, ...num, color: i.owing > 0 ? 'var(--hp-text)' : 'var(--hp-dim)' }}>{i.owing > 0 ? fmtMoney(i.owing) : '—'}</td>
                            <td style={td}><Pill s={i.status} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <span style={labelStyle}>Payments</span>
                      {removedCount > 0 && <label style={{ fontSize: '.74rem', color: 'var(--hp-dim)' }}><input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} /> Show removed ({removedCount})</label>}
                    </div>
                    {!planPays.length ? (
                      <div style={{ fontSize: '.84rem', color: 'var(--hp-dim)', padding: '8px 0' }}>No payments recorded yet.</div>
                    ) : (
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead><tr><th style={th}>Date</th><th style={{ ...th, textAlign: 'right' }}>Amount</th><th style={th}>Ref</th><th style={th} /></tr></thead>
                        <tbody>
                          {planPays.map((p) => (
                            <tr key={p.id} style={{ opacity: p.deletedAt ? 0.45 : 1 }}>
                              <td style={td}>{fmtDate(p.date)}{p.ocrError ? <span title={p.ocrError} style={{ color: AMBER }}> ⚠</span> : ''}</td>
                              <td style={{ ...td, ...num, textDecoration: p.deletedAt ? 'line-through' : 'none' }}>{fmtMoney(p.amount)}{Number(p.fee) > 0 && <div style={{ fontSize: '.7rem', color: 'var(--hp-dim)' }}>+ {fmtMoney(p.fee)} card fee</div>}</td>
                              <td style={{ ...td, fontSize: '.76rem', color: 'var(--hp-dim)' }}>{[p.method, p.receiptNo].filter(Boolean).join(' · ')}</td>
                              <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                {p.file && <a className="btn btn-ghost btn-sm" href={`${DOC_URL}?payment=${p.id}`} target="_blank" rel="noreferrer" title="View receipt"><ExternalLink size={12} /></a>}
                                {p.deletedAt
                                  ? <button className="btn btn-ghost btn-sm" onClick={() => toggleDeleted(p, false)} title="Restore"><Undo2 size={12} /> Restore</button>
                                  : <>
                                    <button className="btn btn-ghost btn-sm" onClick={() => setPayDrawer({ payment: p, planId: p.planId })} title="Edit"><Pencil size={12} /></button>
                                    <button className="btn btn-ghost btn-sm" style={{ color: RED }} onClick={() => toggleDeleted(p, true)} title="Remove"><Trash2 size={12} /></button>
                                  </>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    <div style={{ marginTop: 12, fontSize: '.84rem', lineHeight: 1.6 }}>
                      <div>Due to date: <strong>{fmtMoney(w.dueToDate)}</strong> · paid: <strong>{fmtMoney(w.paid)}</strong></div>
                      <div style={{ color: w.ahead < -0.004 ? RED : GREEN }}>
                        {w.ahead < -0.004 ? <><AlertTriangle size={12} /> Behind by {fmtMoney(-w.ahead)}</> : w.ahead > 0.004 ? <><CheckCircle2 size={12} /> Ahead by {fmtMoney(w.ahead)}</> : <><CheckCircle2 size={12} /> Exactly on schedule</>}
                      </div>
                      {!w.done && w.finishDate && <div style={{ color: 'var(--hp-dim)' }}>Finishes {fmtDate(w.finishDate)} on this schedule · {w.instalmentsLeft} instalment{w.instalmentsLeft === 1 ? '' : 's'} left</div>}
                    </div>
                  </div>
                </div>
                <div style={{ fontSize: '.74rem', color: 'var(--hp-dim)', marginTop: 16, lineHeight: 1.5 }}>
                  The plan total includes the ATO's <em>estimated</em> general interest charge. GIC accrues daily on what is still owed, so paying early
                  makes the real final figure smaller — and a late payment makes it larger. Pay by BPAY (biller {BPAY_BILLER}, reference = the PRN) from the bank with no fee, or by card on EasyPay, which charges a card fee. The ATO's own account statement is the truth: if it shows a
                  different balance, edit the plan total to match. Every schedule and receipt uploaded here is kept for good — removing a payment hides it (Restore brings it back), replacing a receipt keeps the earlier copy, and a finished plan is archived, never deleted. A reminder is pushed to HP Engine a week before each pay-by date, on it, on the due date and daily if overdue. “Pay by” is two business days before the due date, as VLL advised, so a BPAY never lands late.
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {planDrawer && (
        <PlanDrawer plan={planDrawer.plan} notify={addNotification} onClose={() => setPlanDrawer(null)}
          onSaved={(p) => { setPlanDrawer(null); setPlans((ps) => (ps.some((x) => x.id === p.id) ? ps.map((x) => (x.id === p.id ? p : x)) : [...ps, p])); setSelId(p.id); }} />
      )}
      {payDrawer && (
        <PaymentDrawer payment={payDrawer.payment} defaultPlanId={payDrawer.planId} plans={plans} payments={payments} notify={addNotification}
          onClose={() => setPayDrawer(null)}
          onSaved={(p) => { setPayDrawer(null); setPayments((ps) => (ps.some((x) => x.id === p.id) ? ps.map((x) => (x.id === p.id ? p : x)) : [...ps, p])); setSelId(p.planId); }} />
      )}
    </>
  );
}
